<template>
  <div class="mb-3">
    <div v-if="isRunning" class="automation-card">
      <div class="automation-card-header">
        <span class="automation-dot"></span>
        <span class="automation-title flex-1">Interval Automation</span>
        <Button
          icon="pi pi-pencil"
          class="p-button-sm p-button-text p-button-rounded flex-none"
          @click="openEditDialog"
        />
      </div>

      <div class="flex flex-wrap align-items-center gap-4 text-sm">
        <div>
          <div class="text-color-secondary">Start</div>
          <div class="font-semibold">{{ formatValue(change.start_value) }}</div>
        </div>
        <div>
          <div class="text-color-secondary">Current</div>
          <div class="font-semibold">
            {{ formatValue(change.current_value) }}
          </div>
        </div>
        <div>
          <div class="text-color-secondary">Target</div>
          <div class="font-semibold">
            {{ formatValue(change.target_value) }}
          </div>
        </div>
        <div>
          <div class="text-color-secondary">Days left</div>
          <div class="font-semibold">{{ daysLeft }}</div>
        </div>
        <Button
          label="Stop"
          class="p-button-sm p-button-outlined p-button-warning ml-auto"
          :loading="actionLoading"
          @click="confirmStop"
        />
      </div>

      <div v-if="logs.length" class="mt-3">
        <div class="text-sm font-semibold mb-1">Logs</div>
        <ul class="m-0 pl-3 text-sm text-color-secondary">
          <li v-for="log in logs" :key="log.id">
            {{ formatDate(log.timestamp) }} — {{ log.event }}:
            {{ formatValue(log.old_value) }} →
            {{ formatValue(log.new_value) }}
          </li>
        </ul>
      </div>
    </div>

    <Button
      v-else-if="!loading"
      label="Add Automation"
      class="w-full mt-3"
      @click="openAddDialog"
    />

    <Dialog
      v-model:visible="showAddDialog"
      header="Add Automation"
      modal
      style="width: 28rem"
    >
      <div class="flex flex-column gap-3">
        <div>
          <label>Automation type</label>
          <Dropdown
            v-model="automationType"
            :options="AUTOMATION_TYPES"
            optionLabel="label"
            optionValue="value"
            placeholder="Select automation type"
            class="w-full"
          />
        </div>

        <template v-if="automationType === 'interval'">
          <div class="flex gap-3">
            <div style="flex: 1 1 55%">
              <label>Target value</label>
              <InputNumber
                v-model="targetValue"
                :min="0"
                :minFractionDigits="0"
                :maxFractionDigits="4"
                class="w-full"
                :inputStyle="{ width: '100%' }"
              />
            </div>
            <div style="flex: 1 1 45%">
              <label>Over how many days</label>
              <InputNumber
                v-model="days"
                :min="2"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-outlined"
                incrementButtonClass="p-button-outlined"
                incrementButtonIcon="pi pi-plus"
                decrementButtonIcon="pi pi-minus"
                class="w-full"
                :inputStyle="{ width: '100%' }"
              />
            </div>
          </div>
          <small class="text-color-secondary">
            Moves the volume to the target gradually, updating automatically
            once a day. You can stop or edit it any time.
          </small>
        </template>
      </div>
      <template #footer>
        <Button label="Cancel" text @click="showAddDialog = false" />
        <Button
          v-if="automationType === 'interval'"
          label="Start"
          :loading="actionLoading"
          :disabled="
            targetValue == null ||
            !days ||
            days < 2 ||
            Number(targetValue) === Number(currentValue)
          "
          @click="startChange"
        />
      </template>
    </Dialog>

    <Dialog
      v-model:visible="showEditDialog"
      header="Edit Interval Automation"
      modal
      style="width: 28rem"
    >
      <div class="flex flex-column gap-3">
        <div>
          <label>Target value</label>
          <InputNumber
            v-model="editTargetValue"
            :min="0"
            :minFractionDigits="0"
            :maxFractionDigits="4"
            class="w-full"
            :inputStyle="{ width: '100%' }"
          />
        </div>
        <div>
          <label>Over how many days</label>
          <InputNumber
            v-model="editDays"
            :min="2"
            class="w-full"
            :inputStyle="{ width: '100%' }"
          />
        </div>
      </div>
      <template #footer>
        <Button label="Cancel" text @click="showEditDialog = false" />
        <Button
          label="Save"
          :loading="actionLoading"
          :disabled="!isEditValid"
          @click="saveEdit"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import axios from "axios";
import moment from "moment";
import Button from "primevue/button";
import InputNumber from "primevue/inputnumber";
import Dropdown from "primevue/dropdown";
import Dialog from "primevue/dialog";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import { API_BASE_URL } from "@/consts";

// eslint-disable-next-line no-undef
const props = defineProps({
  campaignId: { type: String, required: true },
  currentValue: { type: Number, default: 0 },
});

const AUTOMATION_TYPES = [{ label: "Interval", value: "interval" }];

const toast = useToast();
const confirm = useConfirm();
const token = localStorage.getItem("podcastAdminToken");
const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

const loading = ref(true);
const actionLoading = ref(false);
const change = ref(null);
const logs = ref([]);
const showAddDialog = ref(false);
const automationType = ref(null);
const targetValue = ref(null);
const days = ref(7);
const showEditDialog = ref(false);
const editTargetValue = ref(null);
const editDays = ref(null);

const baseUrl = `${API_BASE_URL}/campaigns/${props.campaignId}/scheduled-change`;

const isRunning = computed(() => change.value?.status === "active");

const isEditValid = computed(
  () => editTargetValue.value != null && !!editDays.value
);

const formatValue = (v) => (v == null ? "-" : Number(v).toFixed(2));
const formatDate = (d) => moment(d).format("MMM D, HH:mm");

const daysLeft = computed(() => {
  if (!change.value) return 0;
  const startDay = moment.utc(change.value.start_date).startOf("day");
  const today = moment.utc().startOf("day");
  const dayNumber = today.diff(startDay, "days");
  return Math.max(0, change.value.days - dayNumber);
});

const fetchState = async () => {
  loading.value = true;
  try {
    const response = await axios.get(baseUrl, authHeaders);
    change.value = response.data.data.change;
    logs.value = response.data.data.logs || [];
  } catch (error) {
    console.error("Error fetching scheduled change:", error);
  }
  loading.value = false;
};

const openAddDialog = () => {
  automationType.value = null;
  targetValue.value = props.currentValue;
  days.value = 7;
  showAddDialog.value = true;
};

const startChange = async () => {
  actionLoading.value = true;
  try {
    await axios.post(
      baseUrl,
      { targetValue: targetValue.value, days: days.value },
      authHeaders
    );
    showAddDialog.value = false;
    toast.add({
      severity: "success",
      summary: "Started",
      detail: "Interval automation started",
      life: 3000,
    });
    await fetchState();
  } catch (error) {
    toast.add({
      severity: "error",
      detail: error.response?.data?.message || "Could not start automation",
      life: 4000,
    });
  }
  actionLoading.value = false;
};

const stopChange = async () => {
  actionLoading.value = true;
  try {
    await axios.patch(`${baseUrl}/stop`, {}, authHeaders);
    await fetchState();
  } catch (error) {
    toast.add({
      severity: "error",
      detail: error.response?.data?.message || "Could not stop automation",
      life: 4000,
    });
  }
  actionLoading.value = false;
};

const confirmStop = () => {
  confirm.require({
    message: `Freeze the volume at ${formatValue(
      change.value.current_value
    )}? No more automatic changes will happen after this.`,
    header: "Stop Confirmation",
    icon: "pi pi-info-circle",
    acceptClass: "p-button-danger",
    accept: stopChange,
  });
};

const openEditDialog = () => {
  editTargetValue.value = Number(change.value.target_value);
  editDays.value = Math.max(2, daysLeft.value || 2);
  showEditDialog.value = true;
};

const saveEdit = async () => {
  actionLoading.value = true;
  try {
    await axios.patch(
      `${baseUrl}/edit`,
      { targetValue: editTargetValue.value, days: editDays.value },
      authHeaders
    );
    showEditDialog.value = false;
    toast.add({
      severity: "success",
      summary: "Updated",
      detail: "Interval automation updated",
      life: 3000,
    });
    await fetchState();
  } catch (error) {
    toast.add({
      severity: "error",
      detail: error.response?.data?.message || "Could not update automation",
      life: 4000,
    });
  }
  actionLoading.value = false;
};

onMounted(fetchState);
</script>

<style scoped>
.automation-card {
  border: 1px solid var(--surface-border);
  border-left: 4px solid var(--primary-color);
  border-radius: 12px;
  padding: 1rem 1.25rem;
}

.automation-card-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.automation-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--primary-color);
  flex: none;
}

.automation-title {
  font-weight: 600;
}
</style>
